import React, { useEffect, useState } from "react"
import { Button, Spinner } from "reactstrap"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ROUTES } from "constants/routes"
import { useOnboarding } from "context/OnboardingContext"
import {
  onboardingPost,
  hasOnboardingRegistration,
  getOnboardingPlanId,
  getOnboardingApiMessage,
  ONBOARDING_PLAN_KEY,
  ONBOARDING_PLAN_SNAPSHOT_KEY,
  ONBOARDING_SUBSCRIPTION_KEY,
  ONBOARDING_INVOICE_KEY,
  clearOnboardingSession,
} from "helpers/onboarding_api_helper"
import OnboardingLayout from "./OnboardingLayout"

const Payment = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    selectedPlanId,
    setSelectedPlanId,
    planSnapshot,
    setPlanSnapshot,
    resetFlow,
  } = useOnboarding()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [redirecting, setRedirecting] = useState(false)

  const verifyPayment = async paymentId => {
    setLoading(true)
    setError("")
    try {
      const res = await onboardingPost("onboarding/payment/verify", {
        paymentId,
        subscriptionId: sessionStorage.getItem(ONBOARDING_SUBSCRIPTION_KEY) || "",
        invoiceId: sessionStorage.getItem(ONBOARDING_INVOICE_KEY) || "",
      })
      if (res?.status) {
        clearOnboardingSession()
        resetFlow()
        navigate(ROUTES.REGISTER_SUCCESS, { replace: true })
      } else {
        setError(
          getOnboardingApiMessage(res) || t("Payment verification failed.")
        )
      }
    } catch {
      setError(t("Payment verification failed."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    document.title = `${t("Payment")} | Wulooj`

    if (!hasOnboardingRegistration()) {
      navigate(ROUTES.REGISTER, { replace: true })
      return
    }

    const storedPlanId = getOnboardingPlanId() || sessionStorage.getItem(ONBOARDING_PLAN_KEY)
    if (storedPlanId && !selectedPlanId) {
      setSelectedPlanId(storedPlanId)
    }

    const storedSnapshot = sessionStorage.getItem(ONBOARDING_PLAN_SNAPSHOT_KEY)
    if (storedSnapshot && !planSnapshot) {
      try {
        setPlanSnapshot(JSON.parse(storedSnapshot))
      } catch {
        sessionStorage.removeItem(ONBOARDING_PLAN_SNAPSHOT_KEY)
      }
    }

    if (!selectedPlanId && !storedPlanId) {
      navigate(ROUTES.REGISTER_PLANS, { replace: true })
    }
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const paymentId = params.get("tap_id") || params.get("paymentId")
    if (paymentId) {
      verifyPayment(paymentId)
    }
  }, [location.search])

  const activePlanId = selectedPlanId || getOnboardingPlanId()

  const startTapPayment = async () => {
    setLoading(true)
    setError("")
    try {
      const res = await onboardingPost("onboarding/payment/tap", {
        planId: activePlanId,
      })
      if (!res?.status) {
        setError(getOnboardingApiMessage(res) || t("Unable to start payment."))
        return
      }
      const data = res.data || {}
      if (data.subscription?._id) {
        sessionStorage.setItem(
          ONBOARDING_SUBSCRIPTION_KEY,
          String(data.subscription._id)
        )
      }
      if (data.invoice?._id) {
        sessionStorage.setItem(ONBOARDING_INVOICE_KEY, String(data.invoice._id))
      }
      if (data.freePlan) {
        clearOnboardingSession()
        resetFlow()
        navigate(ROUTES.REGISTER_SUCCESS, { replace: true })
        return
      }
      if (data.paymentUrl) {
        setRedirecting(true)
        window.location.href = data.paymentUrl
        return
      }
      const chargeId = data.session?.id
      if (chargeId) {
        await verifyPayment(String(chargeId))
        return
      }
      setError(t("Unable to start Tap payment. Please try again."))
    } catch {
      setError(t("Unable to start payment."))
    } finally {
      setLoading(false)
    }
  }

  return (
    <OnboardingLayout>
      <div className="onboarding-card">
        <div className="onboarding-steps">
          <span className="step-pill done">{t("1. Business details")}</span>
          <span className="step-pill done">{t("2. Choose plan")}</span>
          <span className="step-pill active">{t("3. Payment")}</span>
        </div>

        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">{t("Complete your payment")}</h4>
          <span className="tap-badge">
            <i className="bx bx-credit-card" />
            {t("Tap")}
          </span>
        </div>

        {planSnapshot && (
          <div className="onboarding-payment-summary">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">{t("Plan")}</span>
              <strong>{planSnapshot.name}</strong>
            </div>
            <div className="d-flex justify-content-between fw-bold">
              <span>{t("Total")}</span>
              <span>
                {planSnapshot.currency || "SAR"}{" "}
                {Number(planSnapshot.price || 0).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {(loading || redirecting) && (
          <div className="text-center py-3 mb-3">
            <Spinner color="primary" className="me-2" />
            {redirecting
              ? t("Redirecting to Tap secure checkout...")
              : t("Processing payment...")}
          </div>
        )}

        <p className="text-muted mb-4">
          {t(
            "You will be redirected to Tap's secure payment page to complete your subscription. Your card will be saved securely with Tap for automatic monthly renewal — we do not store your card number."
          )}
        </p>

        <div className="d-flex gap-2">
          <Button
            color="primary"
            type="button"
            disabled={loading || redirecting}
            onClick={startTapPayment}
          >
            {loading ? <Spinner size="sm" /> : t("Pay with Tap")}
          </Button>
          <Button
            color="secondary"
            outline
            type="button"
            onClick={() => navigate(ROUTES.REGISTER_PLANS)}
            disabled={loading || redirecting}
          >
            {t("Back")}
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  )
}

export default Payment
